import { Select as AntdSelect } from 'antd';
import type { LabeledValue as ILabeledValue, RefSelectProps, SelectProps } from 'antd/lib/select';
import { CaretDownOutlined, DownOutlined } from '@ant-design/icons';
import Loader from 'components/common/loader';
import React from 'react';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import Divider from 'components/common/divider';
import omit from 'lodash/omit';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import EmptyStateLayout from '../empty-state-layout';
import { FormattedMessage } from 'react-intl';

export interface CustomSelectProps extends SelectProps<any, any> {
  children?: React.ReactNode;
  customActions?: React.ReactNode;
  customNotFoundContent?: React.ReactNode;
  withDivider?: boolean;
  /** Set by `withController` from the form error. Drives antd's own `status` path. */
  invalid?: boolean;
  /** Lifted from `project-tags-selector`'s `&&&` overrides — antd expresses it natively. */
  borderless?: boolean;
  emptyMessageId?: string;
}

export type LabeledValue = ILabeledValue;

export const SelectOption = AntdSelect.Option;
export const SelectOptGroup = AntdSelect.OptGroup;

const Select = React.forwardRef<RefSelectProps, CustomSelectProps>(
  (
    {
      style,
      loading,
      children,
      customActions,
      withDivider,
      customNotFoundContent,
      disabled,
      invalid,
      borderless,
      emptyMessageId = 'no.data',
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const hasCustomActions = !!customActions;
    // Only wrap the panel when there is something to append to it, so the common case keeps antd's own DOM.
    const dropdownRender =
      withDivider || hasCustomActions
        ? (menuNode: React.ReactNode) => (
            <div>
              {menuNode}
              {withDivider && <Divider margin="10px 0px" />}
              {hasCustomActions && <CustomActionsContainer>{customActions}</CustomActionsContainer>}
            </div>
          )
        : undefined;

    const defaultEmptyContent = <EmptyStateLayout description={<FormattedMessage id={emptyMessageId} />} />;

    return (
      <StyledSelect
        notFoundContent={customNotFoundContent ?? (theme.isNewDesign ? defaultEmptyContent : undefined)}
        style={style}
        dropdownRender={dropdownRender}
        popupMatchSelectWidth={false}
        disabled={disabled}
        // antd's status path paints the border from colorErrorHover and the ring from colorErrorOutline,
        // so the error state needs no CSS of its own. New design only: switch-off never had one, and
        // turning it on there would change a screen this ticket does not own.
        status={invalid && theme.isNewDesign ? 'error' : undefined}
        bordered={borderless ? false : undefined}
        // Figma shows a chevron; the caret is what shipped.
        suffixIcon={theme.isNewDesign ? <Chevron /> : <Caret disabled={disabled} />}
        ref={ref}
        {...omit(props, ['hideError'])}>
        {loading ? (
          <SelectOption value="LOADING" disabled>
            <Loader />
          </SelectOption>
        ) : (
          children
        )}
      </StyledSelect>
    );
  }
);

Select.displayName = 'Select';

const CustomActionsContainer = styled.div`
  padding: 8px 30px;
  cursor: pointer;
`;

const Caret = styled(CaretDownOutlined)<{ disabled?: boolean }>`
  color: ${({ theme, disabled }) => (disabled ? theme.nonEditableBorderGray : theme.primaryBlack)} !important;
  pointer-events: none !important;
`;

// No colour of its own: `Select.colorTextQuaternary` in the bridge paints the suffix icon.
const Chevron = styled(DownOutlined)`
  pointer-events: none;
`;

const StyledSelect = styled(AntdSelect)`
  // A declaration rather than the inline style this used to be, so a call site's own width wins.
  width: 100%;

  ${legacyOnly(
    theme => css`
      font-size: 12px !important;
      font-weight: 500 !important;
      color: ${theme.primaryBlack} !important;
      &.ant-select-disabled .ant-select-selector {
        border: 1px solid ${theme.lightGray};
      }
    `
  )}

  ${newDesignOnly(css`
    // The one thing the bridge cannot reach: antd's disabled selector sets background and colour but
    // leaves the border on colorBorder, so the resting and disabled borders would be the same grey.
    &.ant-select-disabled .ant-select-selector {
      border-color: var(--component-field-border-disabled);
    }
  `)}
`;

export default Select;
